import { createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'

import { ID } from 'src/Types/common'
import { Playlist } from 'src/Types/playlists'
import { TrackGenre, TrackLanguage } from 'src/Types/tracks'
import { APP } from 'src/Configs/App'
import { api } from 'src/Services/api'

import { ErrorCode, NameSpace } from '../types'

export interface PlaylistData {
  Playlists: Array<Playlist>
}

interface AddPlaylistWithSpotifyIdResponseItem {
  id: number
  genreName?: string
  languageName?: string
}

interface AddPlaylistWithSpotifyIdResponse {
  id: number
  createdAt: string
  curatorUserId: number
  curatorId: number
  playlistName: string
  playlistUrl: string
  spotifyId: string
  playlistCover: string
  followers: number
  followersUpdatedAt: string
  monthlyListeners: number
  meetsConditions: boolean
  additionType: string
  isBlocked: boolean
  isLimitExceeded: boolean
  isNew: boolean
  isDeactivationNew: boolean
  status: string
  reviewsAmount: number
  discoveredOnArtists: number
  discoveredOnUpdatedAt: string | null
  score: number
  extraScore: number
  level: number
  genresUpdatedAt: string | null
  areGenresUpdatedByAdmin: boolean
  genres: AddPlaylistWithSpotifyIdResponseItem[]
  languages: AddPlaylistWithSpotifyIdResponseItem[]
  tracks: number
}

const mapProperItems = (
  key: 'genreName' | 'languageName',
  items: AddPlaylistWithSpotifyIdResponseItem[] = [],
): Array<{ id: number; value: number; name: string }> =>
  (items || []).map((item) => ({
    id: item.id,
    value: item.id,
    name: item[key] || '',
  }))

const mapToPlaylistGenres = (
  items: AddPlaylistWithSpotifyIdResponseItem[] = [],
): TrackGenre[] =>
  items.map((item) => ({
    genre_id: item.id,
    Genre: { id: item.id, genre_name: item.genreName || '', genre_id: item.id },
  }))

const mapToPlaylistLanguages = (
  items: AddPlaylistWithSpotifyIdResponseItem[] = [],
): TrackLanguage[] =>
  items.map((item) => ({
    language_id: item.id,
    Language: { id: item.id, language_name: item.languageName || '' },
  }))

export interface CuratorPlaylistDto {
  id: number
  createdAt: string
  curatorId: number
  spotifyId: string
  playlistName: string
  playlistCover: string
  followers: number
  monthlyListeners: number
  meetsConditions: boolean
  additionType: string
  isBlocked: boolean
  isLimitExceeded: boolean
  isNew: boolean
  isDeactivationNew: boolean
  status: string
  reviewsAmount: number
  discoveredOnArtists: number
  discoveredOnUpdatedAt: string | null
  score: number
  extraScore: number
  level: number
  tracks: number
  areGenresUpdatedByAdmin: boolean
  genresUpdatedAt: string | null
  genres: AddPlaylistWithSpotifyIdResponseItem[]
  languages: AddPlaylistWithSpotifyIdResponseItem[]
}

export const normalizeCuratorPlaylist = (
  playlist: CuratorPlaylistDto,
): Playlist => ({
  ...playlist,
  curator_id: playlist.curatorId,
  discovered_on_artists: playlist.discoveredOnArtists,
  discovered_on_updated_at: playlist.discoveredOnUpdatedAt ?? '',
  playlist_name: playlist.playlistName,
  playlist_cover: playlist.playlistCover,
  reviews_amount: playlist.reviewsAmount,
  PlaylistGenres: mapToPlaylistGenres(playlist.genres),
  PlaylistLanguages: mapToPlaylistLanguages(playlist.languages),
  PlaylistMoods: [],
  genres: mapProperItems('genreName', playlist.genres),
  languages: mapProperItems('languageName', playlist.languages),
  monthly_listeners: playlist.monthlyListeners,
  meets_conditions: playlist.meetsConditions,
  addition_type: playlist.additionType,
  is_blocked: playlist.isBlocked,
  is_limit_exceeded: playlist.isLimitExceeded,
  extra_score: playlist.extraScore,
  is_deactivation_new: playlist.isDeactivationNew,
})

const normalizeAddedPlaylist = (
  playlist: AddPlaylistWithSpotifyIdResponse,
): Playlist => ({
  ...playlist,
  curator_id: playlist.curatorId,
  discovered_on_artists: playlist.discoveredOnArtists,
  discovered_on_updated_at: playlist.discoveredOnUpdatedAt ?? '',
  playlist_name: playlist.playlistName,
  playlist_cover: playlist.playlistCover,
  playlist_url: playlist.playlistUrl,
  reviews_amount: playlist.reviewsAmount,
  PlaylistGenres: mapToPlaylistGenres(playlist.genres),
  PlaylistLanguages: mapToPlaylistLanguages(playlist.languages),
  PlaylistMoods: [],
  genres: mapProperItems('genreName', playlist.genres),
  languages: mapProperItems('languageName', playlist.languages),
  tracks: playlist.tracks,
  monthly_listeners: playlist.monthlyListeners,
  meets_conditions: playlist.meetsConditions,
  addition_type: playlist.additionType,
  is_blocked: playlist.isBlocked,
  is_limit_exceeded: playlist.isLimitExceeded,
  score: playlist.score,
  extra_score: playlist.extraScore,
  level: playlist.level,
  is_deactivation_new: playlist.isDeactivationNew,
})

export const getPlaylists = createAsyncThunk<PlaylistData>(
  `${NameSpace.Playlists}/getPlaylists`,
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get<CuratorPlaylistDto[]>(
        `${APP.TIKTOK_SERVER}/curator/playlists`,
      )
      return { Playlists: data.map(normalizeCuratorPlaylist) }
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data.message,
        code: response?.status || ErrorCode.InternalServerError,
      })
    }
  },
)
export const addPlaylistWithSpotifyId = createAsyncThunk<Playlist, string>(
  `${NameSpace.Playlists}/addPlaylistWithSpotifyId`,
  async (spotifyId, { rejectWithValue }) => {
    try {
      const { data } = await api.post<AddPlaylistWithSpotifyIdResponse>(
        `${APP.TIKTOK_SERVER}/curator/playlists/add/${spotifyId}`,
      )
      return normalizeAddedPlaylist(data)
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data.message,
        code: response?.status || ErrorCode.InternalServerError,
      })
    }
  },
)
export interface PlaylistLanguagesBody {
  id: ID
  genre_ids: Array<number>
  language_ids: Array<number>
  moods_ids: Array<number>
}
export const editPlaylistsLanguages = createAsyncThunk<
  Playlist[],
  PlaylistLanguagesBody[]
>(
  `${NameSpace.Playlists}/editPlaylistsLanguages`,
  async (playlists, { rejectWithValue }) => {
    try {
      const { data } = await api.put<Playlist[]>(
        `${APP.SERVER}/curator/playlists`,
        {
          playlists,
        },
      )
      return data
    } catch (error) {
      const { response } = error as AxiosError<{ message: string }>
      return rejectWithValue({
        message: response?.data.message,
        code: response?.status || ErrorCode.InternalServerError,
      })
    }
  },
)

interface PlaylistInformationBody {
  playlistID: number | string
  genres: Array<number>
  languages: Array<number>
  moods: Array<number>
}

export const changePlaylistInformation = createAsyncThunk<
  Playlist,
  PlaylistInformationBody
>(
  `${NameSpace.Playlists}/changePlaylistInformation`,
  async ({ playlistID, genres, languages, moods }, { rejectWithValue }) => {
    try {
      const { data } = await api.put<Playlist>(
        `${APP.SERVER}/curator/update-playlist-genres`,
        {
          playlist_id: playlistID,
          genre_ids: genres,
          language_ids: languages,
          moods_ids: moods,
        },
      )
      return data
    } catch (error) {
      const { response } = error as AxiosError<{ errorMsg: string[] }>
      return rejectWithValue({
        message: response?.data.errorMsg[0],
        code: response?.status || ErrorCode.InternalServerError,
      })
    }
  },
)
